
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    CardHeader
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    insurance_company: string;
    policy_number: string;
    start_date: string;
    expiry_date: string;
    premium_amount: string;
    policy_document: FileList;
    remarks?: string;
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AddInsuranceRenewal() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<FormValues>();

    const selectedBranch = watch("branch_id");
    const watchedStart = watch("start_date");
    const watchedExpiry = watch("expiry_date");

    // Fetch Branches on Mount
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const data: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };

        fetchVehicles();
        setValue("vehicle_id", "");
    }, [selectedBranch, setValue]);

    const onSubmit = async (data: FormValues) => {
        setLoading(true);
        try {
            let policyDocName = "";
            if (data.policy_document && data.policy_document.length > 0) {
                const file = data.policy_document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                policyDocName = await uploadFile(file);
            }

            const payload = {
                ...data,
                policy_document: policyDocName,
            };

            await apiPost("insurance_renewal/store", payload);
            toast.success("Insurance Renewal added successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/insurance_renewal`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error adding insurance renewal");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Insurance Renewal" parent="Renewal" title="Add Insurance Renewal" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <H5>Renewal Details</H5>
                            <hr />
                            <Row className="g-3">
                                <Col md={4}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="branch_id"
                                        control={control}
                                        rules={{ required: "Branch is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.branch_id} >
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Vehicle <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_id"
                                        control={control}
                                        rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.vehicle_id} disabled={!selectedBranch}>
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => (
                                                        <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Insurance Company <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="insurance_company"
                                        control={control}
                                        rules={{ required: "Insurance Company is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Company Name" invalid={!!errors.insurance_company} autoComplete="off" />
                                                <FormFeedback>{errors.insurance_company?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Label>Policy Number <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="policy_number"
                                        control={control}
                                        rules={{ required: "Policy Number is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Policy Number" invalid={!!errors.policy_number} autoComplete="off" />
                                                <FormFeedback>{errors.policy_number?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Start Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="start_date"
                                        control={control}
                                        rules={{ required: "Start Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.start_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedExpiry && d && new Date(d) > new Date(watchedExpiry)) {
                                                            toast.error("Start date cannot be after Expiry date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                />
                                                <FormFeedback className="d-block">{errors.start_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Expiry Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="expiry_date"
                                        control={control}
                                        rules={{ required: "Expiry Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.expiry_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedStart && d && new Date(d) < new Date(watchedStart)) {
                                                            toast.error("Expiry date cannot be before Start date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                // minDate={new Date()}
                                                />
                                                <FormFeedback className="d-block">{errors.expiry_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Premium Amount <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="premium_amount"
                                        control={control}
                                        rules={{ required: "Premium Amount is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder="Amount" invalid={!!errors.premium_amount} autoComplete="off" />
                                                <FormFeedback>{errors.premium_amount?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Policy Document <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="policy_document"
                                        control={control}
                                        rules={{ required: "Policy Document is required" }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const file = e.target.files[0];
                                                            if (file.size > 2 * 1024 * 1024) {
                                                                toast.error("File size must be less than 2MB");
                                                                e.target.value = ""; // Clear the input
                                                                onChange(null); // Clear form state
                                                                return;
                                                            }
                                                        }
                                                        onChange(e.target.files);
                                                    }}
                                                    invalid={!!errors.policy_document}
                                                />
                                                <FormFeedback>{errors.policy_document?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={12}>
                                    <Label>Remarks</Label>
                                    <Controller
                                        name="remarks"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Any remarks..." autoComplete="off" />
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <div className="mt-4">
                                <Btn color="primary" disabled={loading} type="submit">
                                    {loading ? <>Submitting <Spinner size="sm" /></> : "Submit"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
